/**************************************************************************************************
 *
 *      @file ./app-entry-main.tsx
 *
 *      App starting point
 *
 *      Triggers setup sequence
 *      Starts Javelin data download
 *      Hydrates store
 *      Mounts React app in DOM
 *      Wraps React app with providers
 */

/******************************************* SETUP *********************************************/
import {signupFormModel, hydrateAppState, polyglot} from './setup/setup';

/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import {Provider as _MobXProvider, observer} from 'mobx-react';

/**************************************** PROJECT MODULES *****************************************/
import './helpers/fetch-helpers';
import {StoreProps} from './types/types';
import {ToastProvider} from './hocs/with-toast-notifications';
import {NavAwayPrompt} from './util-components/nav-away-prompt';

/*************************************** PROJECT COMPONENTS ***************************************/
import {MobxDevtool, RootErrorBoundary, CookieConsentBar, config} from '@canimmunize/core-imports';

import {TopLevelRouter} from './components/top-level-router';

/**************************************** TYPE DEFINITIONS ****************************************/
/**
 * Declare new class type for broken imported MobXProvider type
 */
declare class MobXProviderType extends _MobXProvider {
    render();
}

/**
 * Provider component to make MobX store injectable at any level of the app
 * Allows use of inject higher-order component (@inject)
 *
 * Example use of inject in descendent components of <MobXProvider>:
 *     @inject('appState')
 *     class SomeReactComponent extends React.Component<StoreProps> {
 *         // this.props.appState is now available here
 *         // StoreProps tells Typescript about it
 *     }
 */
const MobXProvider = _MobXProvider as typeof MobXProviderType;

/**************************************** UTIL COMPONENTS *****************************************/
/**
 * Removes the initial loading spinner (indicating load of full app)
 */
const RemoveLoader = () => {
    const loadingSpinner = document.getElementById(`basic-loading-spinner`);
    loadingSpinner.remove();
    return null;
};

/***************************************** MAIN COMPONENT *****************************************/
/**
 * Highest-level component in UI - selects which React component tree to render
 * and sets it up
 *
 * Prep actions performed:
 *     - Wraps the main app in the MobX and Toast providers
 *     - Turns off the load spinner
 *     - Loads cookie consent bar if user has not yet consented to cookies
 *     - Attaches the Mobx Devtool (in development mode)
 *     - Defines the injectable dependency collections & the MobX state store,
 *       and makes them
 *       available through the UI tree via MobX's @inject decorator
 *     - Loads top-level router
 */
@observer
class AppMainEntry extends React.Component<StoreProps> {
    render() {
        return (
            <MobXProvider
                appState={this.props.appState}
                polyglot={polyglot}
                config={config}
                signupFormState={signupFormModel}
            >
                <RootErrorBoundary
                    filename={`[ROOT]/app/client/app-entry-main.tsx`}
                    parentName={`[[ROOT COMPONENT]] AppEntryMain -> MobXProvider`}
                    childTreeInfo={`div -> (MobxDevtool + (ToastProvider -> TopLevelRouter))`}
                    miscInfo={`APP_VERSION :: ${this.props.appState.envVarStore.APP_VERSION}`}
                >
                    <div className={cn(`ovy-hidden`)}>
                        <MobxDevtool />
                        <RemoveLoader />
                        {/*<CookieConsentBar />*/}
                        <NavAwayPrompt />
                        <ToastProvider>
                            <TopLevelRouter />
                        </ToastProvider>
                    </div>
                </RootErrorBoundary>
            </MobXProvider>
        );
    }
}

/******************************* INITIALIZE & MOUNT MAIN COMPONENT ********************************/
/*
 * Builds main component if Javelin data successfully loads
 */

// Attempt to download Javelin data and use it to hydrate store
hydrateAppState
    // Confirm Javelin data downloaded and store hydrated successfully
    .then(data => {
        if (data) {
            return {newAppState: data.newAppState};
        }
        throw new Error(`Data load from javelin failed!`);
    })

    // ERROR HANDLER: Handle Javelin data download or store hydration failure
    .catch((err: Error) => {
        console.error(
            `[ERROR] hydrateAppState :: `,
            `failed to load Javelin data or hydrate store, with error: `,
            err
        );
        throw err;
    })

    // Render/mount app into root DOM element
    .then(data => {
        ReactDOM.render(
            <AppMainEntry appState={data.newAppState} />,
            document.getElementById(`app`)
        );
    })

    // ERROR HANDLER: Handle app render/mount fail
    .catch((err: Error) => {
        console.error(
            `[ERROR] hydrateAppState->ReactDOM.render :: `,
            `failed to mount React app into DOM with error: `,
            err
        );
        throw err;
    });
